import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Form,
  Row,
  Col,
  Image,
  ListGroup,
  Card,
  Button,
  Alert,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import Message from '../components/Message';
import moment from 'moment';
import Meta from '../components/Meta';
import { useTranslation } from 'react-i18next';
import { Helmet } from '@slorber/react-helmet-async';
import { useGetProductDetailsQuery } from '../slices/productsApiSlice';
import { addToCart } from '../slices/cartSlice';
import { getImageUrl } from '../utils/imageUtils';

const ProductScreenOnLine = () => {
  const { id: productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { deviseInfo } = useSelector((state) => state.devise);
  const [qty, setQty] = useState(1);

  const {
    data: product,
    isLoading,
    error,
  } = useGetProductDetailsQuery(productId);

  const currency =
    typeof deviseInfo === 'string' && deviseInfo !== '---' ? deviseInfo : 'TND';

  const displayPrice = () => {
    if (!product) return '';
    if (currency === 'EUR' && product.priceEur != null) return `${product.priceEur} EUR`;
    if (currency === 'USD' && product.priceDol != null) return `${product.priceDol} USD`;
    return `${product.price ?? 0} TND`;
  };

  const addToCartHandler = () => {
    dispatch(addToCart({ ...product, qty }));
    navigate('/cart');
  };

  return (
    <>
      <Link className='btn btn-outline-teal my-3' to='/online'>
        {t('return')}
      </Link>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>
          {error?.data?.message || error.error}
        </Message>
      ) : (
        <>
          <Meta title={product.name} />

          <div className='product-detail'>
            <Row className='g-4 align-items-start'>
              <Col md={5}>
                <div className='product-detail-media'>
                  <Image
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    fluid
                    onError={(e) => {
                      e.target.src = '/images/placeholder.jpg';
                    }}
                  />
                </div>
              </Col>

              <Col md={4}>
                <p
                  className='mb-2 text-uppercase fw-bold'
                  style={{ letterSpacing: '0.06em', fontSize: '0.75rem', color: 'var(--teal-dark)' }}
                >
                  Formation en ligne
                </p>
                <h1 style={{ fontSize: 'clamp(1.4rem, 2vw, 1.9rem)', marginBottom: '0.75rem' }}>
                  {product.name}
                </h1>
                <p className='detail-price mb-3'>{displayPrice()}</p>
                <p style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
                  {product.description}
                </p>
                <p className='product-meta mb-0'>
                  {t('Date')} {moment(product.date).format('DD MMMM YYYY')}
                </p>
              </Col>

              <Col md={3}>
                <div className='product-buy-card'>
                  <ListGroup variant='flush'>
                    <ListGroup.Item>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span>{t('Price')}</span>
                        <strong className='detail-price' style={{ fontSize: '1.15rem' }}>
                          {displayPrice()}
                        </strong>
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div className='d-flex justify-content-between'>
                        <span>{t('status')}</span>
                        <strong>
                          {product.countInStock > 0 ? t('onstock') : t('notonstock')}
                        </strong>
                      </div>
                    </ListGroup.Item>

                    {product.countInStock > 0 && (
                      <ListGroup.Item>
                        <Form.Label className='mb-1'>{t('qty')}</Form.Label>
                        <Form.Control
                          as='select'
                          value={qty}
                          onChange={(e) => setQty(Number(e.target.value))}
                        >
                          {[...Array(product.countInStock).keys()].map((x) => (
                            <option key={x + 1} value={x + 1}>
                              {x + 1}
                            </option>
                          ))}
                        </Form.Control>
                      </ListGroup.Item>
                    )}

                    <ListGroup.Item>
                      <Button
                        className='btn-teal w-100'
                        type='button'
                        disabled={product.countInStock === 0}
                        onClick={addToCartHandler}
                      >
                        {t('addcart')}
                      </Button>
                    </ListGroup.Item>
                  </ListGroup>
                </div>
              </Col>
            </Row>

            {product.type === 1 && (
              <Row className='mt-4'>
                <Col>
                  <Card>
                    <Card.Header className='bg-primary text-white'>
                      <h5 className='mb-0'>Formation en ligne</h5>
                    </Card.Header>
                    <Card.Body>
                      <Alert variant='info'>
                        <strong>Formation accessible après achat</strong>
                        <br />
                        Cette formation en ligne vous donnera accès à du contenu vidéo
                        et des ressources pédagogiques une fois votre commande validée.
                      </Alert>
                      <div className='p-3 rounded' style={{ background: 'var(--surface)' }}>
                        <h6>Comment ça marche ?</h6>
                        <ol className='mb-0'>
                          <li>Ajoutez cette formation à votre panier</li>
                          <li>Procédez au paiement</li>
                          <li>Une fois le paiement confirmé, vous recevrez un accès</li>
                          <li>Connectez-vous pour accéder à votre espace formation</li>
                        </ol>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <Helmet>
              <title>Licorne Academy</title>
              <meta name='description' content='Licorne Academy' />
              <link rel='canonical' href='https://www.fr.licorne-academy.com/' />
            </Helmet>
          </div>
        </>
      )}
    </>
  );
};

export default ProductScreenOnLine;
